import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import {
  LayoutDashboard, Store, Package, Users, ShoppingBag,
  Ticket, Wallet, Settings, LogOut, Menu, X, ChevronDown, FolderOpen
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';

const navItems = [
  { key: 'dashboard', path: '/app', icon: LayoutDashboard },
  { key: 'stores', path: '/app/stores', icon: Store },
  { key: 'categories', path: '/app/categories', icon: FolderOpen },
  { key: 'products', path: '/app/products', icon: Package },
  { key: 'vendors', path: '/app/vendors', icon: ShoppingBag },
  { key: 'customers', path: '/app/customers', icon: Users },
  { key: 'vouchers', path: '/app/vouchers', icon: Ticket },
  { key: 'withdrawals', path: '/app/withdrawals', icon: Wallet },
  { key: 'settings', path: '/app/settings', icon: Settings },
];

export const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      isActive
        ? 'bg-[#EB7B44] text-white shadow-sm'
        : 'text-[#64584F] hover:bg-[#F6F5F5] hover:text-[#202020]'
    }`;

  const content = (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-[#DFDEDE]">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#EB7B44] flex items-center justify-center">
            <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>S</span>
          </div>
          <span className="text-xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Sellmu</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map(item => (
          <NavLink
            key={item.key}
            to={item.path}
            className={linkClass}
            end={item.path === '/app'}
            onClick={onMobileClose}
            data-testid={`nav-${item.key}`}
          >
            <item.icon size={18} strokeWidth={1.5} />
            <span>{t(item.key)}</span>
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-[#DFDEDE]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#EB7B44]/10 flex items-center justify-center text-[#EB7B44] font-semibold text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#202020] truncate">{user?.name}</p>
            <p className="text-xs text-[#A6A2A0] truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-[#64584F] hover:text-red-600 hover:bg-red-50"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut size={16} className="mr-2" />
          {t('logout')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[260px] bg-[#FEFEFE] border-r border-[#CACACC] h-screen fixed left-0 top-0 z-30">
        {content}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={onMobileClose} />
          <aside className="absolute left-0 top-0 h-full w-[280px] bg-[#FEFEFE] shadow-2xl animate-slide-in">
            <div className="flex justify-end p-2">
              <button onClick={onMobileClose} className="p-2 rounded-lg hover:bg-[#F6F5F5]">
                <X size={20} className="text-[#64584F]" />
              </button>
            </div>
            {content}
          </aside>
        </div>
      )}
    </>
  );
};

export const MobileMenuButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden p-2 rounded-lg hover:bg-[#F6F5F5] transition-colors"
    data-testid="mobile-menu-btn"
  >
    <Menu size={22} className="text-[#64584F]" />
  </button>
);
